import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Users, MessageCircle, Star, Filter, X, Swords, Target, Flame, Shield, TrendingUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getAvatarUrl } from '../utils/avatarUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const levelNames = ['Principiante', 'Base', 'Intermedio', 'Avanzato', 'Esperto'];
const levelColors = [
  'bg-slate-100 text-slate-600',
  'bg-green-100 text-green-600',
  'bg-sky-100 text-sky-600',
  'bg-purple-100 text-purple-600',
  'bg-amber-100 text-amber-600'
];

const fasciaColors = {
  1: 'from-amber-400 to-orange-500',
  2: 'from-purple-400 to-indigo-500',
  3: 'from-sky-400 to-blue-500',
  4: 'from-emerald-400 to-teal-500',
  5: 'from-slate-400 to-slate-500',
};

const LevelBadge = ({ level }) => (
  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${levelColors[level - 1] || levelColors[0]}`}>
    {levelNames[level - 1] || 'N/D'}
  </span>
);

const PlayerModal = ({ playerId, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!playerId) return;
    const fetchStats = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/users/${playerId}/stats`, { withCredentials: true });
        setStats(res.data);
      } catch (err) {
        console.error('Error fetching player stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [playerId]);

  if (!playerId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative bg-white w-full sm:w-[420px] max-h-[85vh] rounded-t-3xl sm:rounded-3xl overflow-y-auto animate-slideUp"
        onClick={e => e.stopPropagation()}
        data-testid="player-stats-modal"
      >
        {loading ? (
          <div className="p-8 space-y-4">
            <div className="w-20 h-20 bg-slate-200 rounded-2xl mx-auto animate-pulse" />
            <div className="h-5 w-40 bg-slate-200 rounded mx-auto animate-pulse" />
            <div className="grid grid-cols-3 gap-3">
              {[1,2,3].map(i => <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />)}
            </div>
          </div>
        ) : stats ? (
          <>
            {/* Header gradient */}
            <div className={`relative bg-gradient-to-br ${fasciaColors[stats.fascia] || 'from-slate-400 to-slate-500'} p-6 pb-14`}>
              <button
                onClick={onClose}
                data-testid="close-player-modal"
                className="absolute top-3 right-3 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
              <div className="text-center">
                <p className="text-white/70 text-xs font-medium uppercase tracking-wider mb-1">Scheda Giocatore</p>
              </div>
            </div>

            {/* Avatar overlapping */}
            <div className="relative -mt-10 flex flex-col items-center px-6">
              <div className="w-20 h-20 bg-white rounded-2xl shadow-lg flex items-center justify-center text-2xl font-bold text-sky-500 overflow-hidden ring-4 ring-white">
                {getAvatarUrl(stats) ? (
                  <img src={getAvatarUrl(stats)} alt={stats.name} className="w-full h-full object-cover rounded-2xl" />
                ) : (
                  stats.name?.charAt(0)
                )}
              </div>
              <h3 className="font-outfit text-xl font-bold text-slate-900 mt-3">
                {stats.name} {stats.surname || ''}
              </h3>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap justify-center">
                <LevelBadge level={stats.level || 1} />
                {stats.age && <span className="text-xs text-slate-400">{stats.age} anni</span>}
                {stats.gender && <span className="text-xs text-slate-400">{stats.gender === 'M' ? 'Uomo' : 'Donna'}</span>}
              </div>
              {stats.fascia > 0 && (
                <div className="mt-2 flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-sm font-semibold text-slate-700">Fascia {stats.fascia} — {stats.fascia_name}</span>
                  <span className="text-xs text-slate-400 ml-1">({stats.ranking_points} pts)</span>
                </div>
              )}
            </div>

            {/* Stats grid */}
            <div className="px-5 pb-5 mt-5 space-y-4">
              {/* Main stats */}
              <div className="grid grid-cols-3 gap-2.5">
                <div className="bg-emerald-50 rounded-xl p-3 text-center" data-testid="stat-wins">
                  <p className="text-2xl font-bold text-emerald-600">{stats.wins}</p>
                  <p className="text-xs text-emerald-500 font-medium mt-0.5">Vittorie</p>
                </div>
                <div className="bg-red-50 rounded-xl p-3 text-center" data-testid="stat-losses">
                  <p className="text-2xl font-bold text-red-500">{stats.losses}</p>
                  <p className="text-xs text-red-400 font-medium mt-0.5">Sconfitte</p>
                </div>
                <div className="bg-sky-50 rounded-xl p-3 text-center" data-testid="stat-total">
                  <p className="text-2xl font-bold text-sky-600">{stats.total_matches}</p>
                  <p className="text-xs text-sky-500 font-medium mt-0.5">Totali</p>
                </div>
              </div>

              {/* Detailed stats */}
              <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-amber-500" />
                    <span className="text-sm text-slate-600">Win Rate</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-400 rounded-full transition-all" style={{width: `${stats.win_rate}%`}} />
                    </div>
                    <span className="text-sm font-bold text-slate-800 w-10 text-right">{stats.win_rate}%</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Swords className="w-4 h-4 text-sky-500" />
                    <span className="text-sm text-slate-600">Sfide Classifica</span>
                  </div>
                  <span className="text-sm font-bold text-slate-800">{stats.classifica_matches}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-500" />
                    <span className="text-sm text-slate-600">Amichevoli</span>
                  </div>
                  <span className="text-sm font-bold text-slate-800">{stats.amichevoli}</span>
                </div>

                {stats.scalpi > 0 && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-orange-500" />
                      <span className="text-sm text-slate-600">Scalpi</span>
                    </div>
                    <span className="text-sm font-bold text-orange-600">{stats.scalpi}</span>
                  </div>
                )}

                {stats.current_streak > 0 && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Flame className="w-4 h-4 text-red-500" />
                      <span className="text-sm text-slate-600">Serie Vittorie</span>
                    </div>
                    <span className="text-sm font-bold text-red-600">{stats.current_streak}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <Link
                to={`/chat?user=${stats.user_id}`}
                data-testid="modal-chat-player"
                className="flex items-center justify-center gap-2 w-full py-3 bg-sky-400 text-white rounded-2xl font-semibold hover:bg-sky-500 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                Scrivi Messaggio
              </Link>
            </div>
          </>
        ) : (
          <div className="p-8 text-center">
            <p className="text-slate-500">Impossibile caricare i dati</p>
          </div>
        )}
      </div>
    </div>
  );
};

const RankingPage = () => {
  const { user } = useAuth();
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('M');
  const [levelFilter, setLevelFilter] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);

  useEffect(() => {
    fetchRanking();
  }, [category, levelFilter]);

  const fetchRanking = async () => {
    setLoading(true);
    try {
      const isUnder20 = category.startsWith('U20');
      const gender = category.includes('F') ? 'F' : 'M';
      let url = `${API_URL}/api/ranking?gender=${gender}`;
      if (isUnder20) url += '&under20=true';
      if (levelFilter) url += `&level=${levelFilter}`;
      const response = await axios.get(url, { withCredentials: true });
      setRanking(response.data);
    } catch (error) {
      console.error('Error fetching ranking:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return 'bg-amber-400 text-white';
    if (rank === 2) return 'bg-slate-400 text-white';
    if (rank === 3) return 'bg-amber-700 text-white';
    return 'bg-slate-100 text-slate-600';
  };

  const categoryTabs = [
    { key: 'M', label: 'Maschile', color: 'bg-sky-400' },
    { key: 'F', label: 'Femminile', color: 'bg-pink-400' },
    { key: 'U20M', label: 'Under 20 M', color: 'bg-emerald-400' },
    { key: 'U20F', label: 'Under 20 F', color: 'bg-rose-400' },
  ];

  const emptyMessages = {
    M: 'Nessun giocatore trovato',
    F: 'Nessuna giocatrice trovata',
    U20M: 'Nessun giocatore Under 20 trovato',
    U20F: 'Nessuna giocatrice Under 20 trovata',
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 animate-fadeIn" data-testid="ranking-page">
      {/* Header */}
      <div>
        <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-slate-900 flex items-center gap-3">
          <Trophy className="w-8 h-8 text-amber-500" />
          Classifica
        </h1>
        <p className="text-slate-500 mt-1">Classifica ufficiale del circolo</p>
      </div>

      {/* Category Tabs */}
      <div className="bg-white rounded-2xl p-1.5 border border-slate-100 grid grid-cols-2 gap-1.5">
        {categoryTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => { setCategory(tab.key); setLevelFilter(null); }}
            data-testid={`tab-${tab.key}`}
            className={`py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
              category === tab.key
                ? `${tab.color} text-white shadow-md`
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Level Filter */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <Filter className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <button
          onClick={() => setLevelFilter(null)}
          data-testid="filter-all"
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex-shrink-0 ${
            !levelFilter ? 'bg-sky-400 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
          }`}
        >
          Tutti
        </button>
        {[1, 2, 3, 4, 5].map(lv => (
          <button
            key={lv}
            onClick={() => setLevelFilter(levelFilter === lv ? null : lv)}
            data-testid={`filter-level-${lv}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex-shrink-0 flex items-center gap-1 ${
              levelFilter === lv ? 'bg-sky-400 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            <Star className={`w-3 h-3 ${levelFilter === lv ? 'fill-white' : ''}`} />
            {levelNames[lv - 1]}
          </button>
        ))}
      </div>

      {/* Ranking List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-20 bg-slate-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : ranking.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 border border-slate-100 text-center">
          <Users className="w-16 h-16 text-slate-200 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">{emptyMessages[category]}</p>
          <p className="text-slate-400 text-sm mt-2">
            {levelFilter ? `Nessun giocatore di livello ${levelNames[levelFilter - 1]}` : 'I giocatori devono aggiornare il profilo'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {ranking.map((player, index) => {
            const isCurrentUser = player.user_id === user?.user_id;
            const displayRank = player.ranking_position > 0 ? player.ranking_position : null;
            return (
              <button
                key={player.user_id}
                onClick={() => setSelectedPlayerId(player.user_id)}
                data-testid={`ranking-player-${index}`}
                className={`w-full text-left bg-white rounded-2xl p-4 border transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer ${
                  isCurrentUser ? 'border-sky-300 bg-sky-50/50' : 'border-slate-100'
                }`}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  {/* Position */}
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center font-bold text-base sm:text-lg flex-shrink-0 ${
                    displayRank ? getRankStyle(displayRank) : 'bg-slate-50 text-slate-300'
                  }`}>
                    {displayRank || '-'}
                  </div>

                  {/* Avatar */}
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-sky-400 rounded-xl flex items-center justify-center text-white font-semibold overflow-hidden flex-shrink-0">
                    {getAvatarUrl(player) ? (
                      <img src={getAvatarUrl(player)} alt={player.name} className="w-full h-full rounded-xl object-cover" />
                    ) : (
                      player.name?.charAt(0)
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-slate-900 truncate">
                        {player.name} {player.surname || ''}
                      </p>
                      {isCurrentUser && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-sky-100 text-sky-600">Tu</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <LevelBadge level={player.level || 1} />
                      {player.age && <span className="text-xs text-slate-400">{player.age} anni</span>}
                    </div>
                  </div>

                  {/* Stats (desktop) */}
                  <div className="hidden sm:flex items-center gap-6 text-center">
                    <div>
                      <p className="text-lg font-semibold text-slate-900">{player.completed_matches || 0}</p>
                      <p className="text-xs text-slate-500">Partite</p>
                    </div>
                  </div>

                  {/* Chevron indicator */}
                  <div className="w-6 h-6 flex items-center justify-center text-slate-300 flex-shrink-0">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Player Stats Modal */}
      <PlayerModal
        playerId={selectedPlayerId}
        onClose={() => setSelectedPlayerId(null)}
      />
    </div>
  );
};

export default RankingPage;
